import {Button, Card, Input, Skeleton, Tag, Typography} from "antd";
import {EditOutlined, SyncOutlined} from "@ant-design/icons";
import type {Raid} from "@/raids/rest/RaidSchema.ts";
import {formatDateTime} from "@/helpers/dateFormatHelper.ts";
import {LabeledInput} from "@/components/LabeledInput.tsx";
import {useEffect, useState} from "react";
import {localizedNumberFormatter} from "@/helpers/numberFormatHelper.tsx";
import {raidDuration} from "@/helpers/raidHelper.ts";
import { SpiceRefining } from "./SpiceRefining";

type Props = {
  raid: Raid | null,
  isLoading: boolean,
  isSaving: boolean,
  isEditAble: boolean,
  callBackSaveName?: (name: string) => void,
  /** Nur Bearbeitungsroute: gespeicherte Admin-PIN zur Anzeige (copyable) */
  editPin?: string,
}

export function RaidOverView({raid, isLoading, isSaving, callBackSaveName, isEditAble, editPin}: Props) {

  const [name, setName] = useState(raid?.name ?? "");
  const canEditName = isEditAble && raid?.status !== "CLOSED";

  useEffect(() => {
    if (!raid) return;
    setName(raid.name);
  }, [raid]);

  function changeRaidName() {
    if (!canEditName) return;
    if (!name || name.length === 0) return;
    callBackSaveName?.(name);
  }

  return (
      <div>
        <Card>
          <div className={"pb-2 [&_.ant-input-group-addon]:p-0!"} data-tour="raid-name">
            <LabeledInput label={"Name"} isLoading={isLoading}>
              {!canEditName ? (
                  <Typography.Text className="text-base!">
                    {raid?.name ?? "—"}
                  </Typography.Text>
              ) : (
                  <Input disabled={isSaving}
                         onPressEnter={() => {
                           changeRaidName();
                         }}
                         value={name}
                         onChange={(e) => setName(e.target.value)}
                         addonAfter={
                           <Button color="default"
                                   disabled={isSaving}
                                   className={"h-7!"}
                                   variant="text"
                                   icon={<EditOutlined/>}
                                   loading={isSaving && {icon: <SyncOutlined spin/>}}
                                   onClick={() => {
                                     changeRaidName();
                                   }}
                           />
                         }/>
              )}
            </LabeledInput>
            {editPin && isEditAble && (
                <div className="pt-2">
                  <Typography.Text type="secondary" className="text-sm">
                    Admin-PIN:{" "}
                  </Typography.Text>
                  <Typography.Text copyable={{ text: editPin }}>{editPin}</Typography.Text>
                </div>
            )}
          </div>
          <div className={"flex flex-wrap"} data-tour="raid-status">
            {isLoading && <Skeleton title={false} paragraph={{rows: 1}} active className={"w-full"}/>}
            {!isLoading &&
                <>
                <SpiceRefining raid={raid} disabled={isSaving || !raid}/>
                  {raid?.status === "RUNNING" &&
                      <Tag color="processing">Gestartet am {formatDateTime(raid?.startTime)}</Tag>
                  }
                    {raid?.status === "CLOSED" &&
                        <>
                          <Tag color="processing">Beendet am {formatDateTime(raid?.endTime)}</Tag>
                          <Tag color="processing">Dauer {raidDuration(raid?.startTime, raid?.endTime)} Minuten</Tag>
                        </>

                    }
                    <Tag color="purple">Spice: {localizedNumberFormatter(raid?.spiceReadOnly)}</Tag>
                    <Tag color="purple">Titan: {localizedNumberFormatter(raid?.titaniumReadOnly)}</Tag>
                    <Tag color="purple">Stravidium {localizedNumberFormatter(raid?.stravidiumReadOnly)}</Tag>
                </>
            }
          </div>
        </Card>
      </div>
  )
}